import { apiServer } from "../config/config";
import { SyncBusinessRespose } from "../types";
import { fetchWithToken } from "./fetchWithToken";
import { NearbyBusinessesResponse } from "../types/nearby-businesses";
import { BusinessInterestType } from "../types/tagging";
export async function fetchBusinessTypes() {
  return await fetch(apiServer + "/business/load-business-types");
}

export async function fetchBusinessTypesFrontend() {
  return await fetch(apiServer + "/business/load-business-types-frontend");
}

export async function addMapperNotes(
  venueId: string,
  notes: string,
  businessReportIssueAction: string,
  token: string,
  returnOnDate?: string,
  actionOnDate?: string,
  locationDetails?: Array<{ location_type: string; location_id: string }>,
) {
  try {
    const formData = new FormData();
    formData.append("business_id", venueId);
    formData.append("notes", notes);

    // Use locationDetails array if provided, otherwise fall back to single location
    if (locationDetails && locationDetails.length > 0) {
      locationDetails.forEach((location, index) => {
        formData.append(
          `location_details[${index}][location_type]`,
          location.location_type,
        );
        formData.append(
          `location_details[${index}][location_id]`,
          location.location_id,
        );
      });
    }

    formData.append("businessReportIssueAction", businessReportIssueAction);
    if (returnOnDate) {
      formData.append("return_on_date", returnOnDate);
    }
    if (actionOnDate) {
      formData.append("action_on_date", actionOnDate);
    }
    console.log(formData);
    const response = await fetch(`${apiServer + "/review/report-venue"}`, {
      method: "POST",
      headers: {
        Authorization: "Bearer " + token,
      },
      body: formData,
    });
    const data = await response.json();
    console.log(data);
    if (data.status === "error") {
      return data.message;
    } else {
      return data.data;
    }
  } catch (error: any) {
    return error.message;
  }
}

export async function closeMapperNote(issueId: string, accessToken: string) {
  try {
    const formData = new FormData();
    formData.append("issueId", issueId);
    const response = await fetch(
      `${apiServer + "/business/close-mapper-note"}`,
      {
        method: "POST",
        headers: {
          Authorization: "Bearer " + accessToken,
        },
        body: formData,
      },
    );
    const data = await response.json();
    if (data.status === "error") {
      return data.message;
    } else {
      return data;
    }
  } catch (error: any) {
    console.log(`closeMapperNote error: ${error}`);
    return error.message;
  }
}

export async function syncBusiness(businessId: string, accessToken: string) {
  const formData = new FormData();
  formData.append("business_id", businessId);
  const response = await fetchWithToken(apiServer + "/business/sync-business", {
    method: "POST",
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
    body: formData,
  });
  const data = await response?.json();
  if (data.status === "error") {
    throw new Error(data.message);
  }
  return data as {
    status: string;
    message: string;
    data: SyncBusinessRespose;
  };
}

export async function trackGuideUsage(businessId: number, accessToken: string) {
  try {
    const formData = new FormData();
    formData.append("business_id", String(businessId));
    await fetchWithToken(apiServer + "/business/track-guide-usage", {
      method: "POST",
      headers: { Authorization: `Bearer ${accessToken}` },
      body: formData,
    });
  } catch (err) {
    console.warn("trackGuideUsage failed", err);
  }
}

export async function fetchNearbyBusinesses(
  businessId: string,
): Promise<NearbyBusinessesResponse> {
  const formData = new FormData();
  formData.append("business_id", businessId);

  const response = await fetch(
    `${apiServer}/business/near-by-businesses-details`,
    {
      method: "POST",
      body: formData,
    },
  );

  const data = await response.json();
  if (data.status === "error") {
    throw new Error(data.message);
  }

  if (data.data?.nearbyBusinessList) {
    data.data.nearbyBusinessList = data.data.nearbyBusinessList.map(
      (v: any) => {
        if (v?.business) {
          v.business.isComplex = v.business.is_complex;
        }
        return v;
      },
    );
    console.log(
      "[fetchNearbyBusinesses] first venue isComplex:",
      data.data.nearbyBusinessList[0]?.business?.isComplex,
    );
  }

  return data;
}

export async function addClaims(
  venueId: string,
  name: string,
  email: string,
  phone: string,
  description: string,
  flagVenueAccessibilityId: string | null,
  token: string,
) {
  try {
    const formData = new FormData();
    formData.append("business_id", venueId);
    formData.append("name", name);
    formData.append("email", email);
    formData.append("phone", phone);
    formData.append("description", description);
    if (flagVenueAccessibilityId) {
      formData.append("flag_venue_accessibility_id", flagVenueAccessibilityId);
    }

    console.log(formData);
    const response = await fetch(
      `${apiServer + "/business/create-business-claim"}`,
      {
        method: "POST",
        headers: {
          Authorization: "Bearer " + token,
        },
        body: formData,
      },
    );
    const data = await response.json();
    return data;
  } catch (error: any) {
    return error.message;
  }
}

export async function loadBusinessInterests(
  token: string,
  includePhotos: boolean = true,
) {
  const queryParams = new URLSearchParams({
    sort_by: "created_at",
    sort_order: "desc",
    include_photos: includePhotos.toString(),
  });

  // Adding query parameters to the URL
  const url = `${apiServer}/user/business-interests?${queryParams.toString()}`;

  try {
    const response = await fetch(url, {
      method: "GET",
      headers: {
        Authorization: "Bearer " + token,
      },
    });
    const data = await response.json();
    return data as {
      status: string;
      message: string;
      data: BusinessInterestType[];
    };
  } catch (error: any) {
    console.log(`loadBusinessInterests error: ${error}`);
    return {
      status: "error",
      message: error.message,
      data: [],
    };
  }
}

export async function syncBusinessInterests(
  businessId: string,
  interestType: string,
  notifyAccessibilityInformation: boolean,
  notifyLiveAlert: boolean,
  token: string,
  action: "create" | "update" | "delete",
  interestId?: string,
) {
  try {
    const formData = new FormData();
    formData.append("business_id", businessId);
    formData.append("interested_type", interestType);
    formData.append(
      "notify_accessibility_information",
      notifyAccessibilityInformation ? "1" : "0",
    );
    formData.append("notify_live_alert", notifyLiveAlert ? "1" : "0");
    if (interestId) {
      formData.append("id", interestId);
    }
    formData.append("action", action);
    console.log(formData);
    const response = await fetch(`${apiServer}/business/sync-subscription`, {
      method: "POST",
      headers: {
        Authorization: "Bearer " + token,
      },
      body: formData,
    });
    const data = await response.json();
    console.log(JSON.stringify(data));
    return data;
  } catch (error: any) {
    return error.message;
  }
}

export interface MyVenuesResponse {
  status: string;
  data: {
    id: number;
    slug: string;
    name: string;
    address: string;
    dashboard_urls: string[] | null;
  }[];
  meta: {
    total: number;
    per_page: number;
    current_page: number;
    last_page: number;
  };
}

export async function fetchMyVenues(
  token: string,
  page: number = 1,
  per_page: number = 10,
): Promise<MyVenuesResponse> {
  const response = await fetchWithToken(
    `${apiServer}/business/my-venues?page=${page}&per_page=${per_page}`,
    {
      method: "GET",
      headers: { Authorization: `Bearer ${token}` },
    },
  );

  if (!response || !response.ok) {
    throw new Error("Failed to fetch venues");
  }

  const data = await response.json();
  if (data.status === "error") {
    throw new Error(data.message || "Unknown error");
  }
  return data;
}

export type BusinessOpeningHourInput = {
  day: number;
  open_time: string;
  close_time: string;
};

export type UpdateBusinessGeneralDetailsPayload = {
  name: string;
  address: string;
  phone?: string | null;
  website?: string | null;
  description?: string;
  opening_hours?: BusinessOpeningHourInput[];
};

export type UpdateBusinessGeneralDetailsResponse = {
  status: string;
  message: string;
  data: { business_id: number } | [];
};

export async function updateBusinessGeneralDetails(
  businessId: number | string,
  payload: UpdateBusinessGeneralDetailsPayload,
  accessToken: string,
): Promise<UpdateBusinessGeneralDetailsResponse> {
  const response = await fetchWithToken(
    `${apiServer}/business/${businessId}/info`,
    {
      method: "PUT",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify(payload),
    },
  );

  if (!response) {
    throw new Error("Failed to update business details");
  }

  const data = await response.json();
  if (data.status === "error") {
    throw new Error(data.message || "Failed to update business details");
  }
  return data as UpdateBusinessGeneralDetailsResponse;
}
